import { Circle as CircleIcon } from '@mui/icons-material';
import { identity, orderBy, uniqueId } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModules } from 'hooks';
import {
  Divider,
  Drawer,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  styled,
} from 'netline-ui';
import services from 'services';
import { OthersMenuItem } from 'types';
import { getBaseTitle } from 'utils';

import { createMenuItem } from '../SidebarNavigation/SidebarNavigation.utils';

import styles, { mobileNavigationClasses } from './MobileNavigation.styles';
import MobileOthersMenu from './MobileOthersMenu/MobileOthersMenu';
import MobilePreferences from './MobilePreferences/MobilePreferences';
import MobilePreferencesMenuItem from './MobilePreferences/MobilePreferencesMenuItem';
import MobileProfileMenuDialog from './MobileProfileMenuDialog';
import MobileUserInfo from './MobileUserInfo';
import useOthersItems from './useOthersItems';

const MobileNavigationRoot = styled(Drawer, { name: mobileNavigationClasses.$name })(styles);

const handleToggleMobileNavigation = () => {
  services.appFrame.mobileNavigation.toggle();
};

const MobileNavigation = () => {
  const {
    t,
    i18n: { exists },
  } = useTranslation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const others = useOthersItems();
  const hasPreferences = services.appFrame.preferences.getItems().length > 0;

  const {
    mobileNavigation: { appModules, open },
  } = useModules();

  const applications = useMemo(
    () => appModules.map((item) => createMenuItem(item, { t, exists })),
    [appModules, t, exists],
  );

  const handleOpenProfileMenuClick = useCallback(() => {
    setProfileMenuOpen(true);
    services.appFrame.mobileNavigation.toggle();
  }, []);

  const handleCloseProfileMenuClick = useCallback(() => {
    setProfileMenuOpen(false);
  }, []);

  const handleLogoutClick = useCallback(() => {
    services.appFrame.auth.logout();
  }, []);

  const handlePreferencesMenuClick = useCallback(() => {
    setPreferencesOpen(true);
    services.appFrame.mobileNavigation.toggle();
  }, []);

  const handleClosePreferencesMenuClick = useCallback(() => {
    setPreferencesOpen(false);
  }, []);

  const othersItems: OthersMenuItem[] = useMemo(
    () =>
      orderBy(
        [
          ...others,
          {
            type: 'COMPONENT',
            Component: MobilePreferencesMenuItem,
            order: 3000,
            ComponentProps: {
              onClick: handlePreferencesMenuClick,
            },
            id: uniqueId('othersItems'),
          },
        ],
        'other',
      ).filter(identity) as OthersMenuItem[], // as filter removes any null or undefined elements
    [handlePreferencesMenuClick, others],
  );

  return (
    <>
      <MobileNavigationRoot
        className={mobileNavigationClasses.root}
        classes={{ paper: mobileNavigationClasses.paper }}
        anchor="left"
        open={open}
        onClose={handleToggleMobileNavigation}
      >
        <div className={mobileNavigationClasses.headerLine}></div>
        <div className="MobileNavigation-header">
          <CircleIcon fontSize="small" className={mobileNavigationClasses.headerIcon} />
          <span>{getBaseTitle()}</span>
        </div>
        {!!appModules.length && (
          <List
            aria-labelledby="applications-subheader"
            subheader={
              <ListSubheader component="div" id="applications-subheader">
                <div className="MobileNavigation-appHeader">{t('mobileNavigation.applications')}</div>
              </ListSubheader>
            }
          >
            {applications.map(({ id, text, link, Icon, component }) => (
              <ListItemButton key={id} component={component || Link} to={link} onClick={handleToggleMobileNavigation}>
                {Icon && (
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                )}
                <ListItemText>{text}</ListItemText>
              </ListItemButton>
            ))}
          </List>
        )}
        {(!!others.length || hasPreferences) && (
          <>
            <Divider />
            <MobileOthersMenu items={othersItems} />
          </>
        )}
        <div className={mobileNavigationClasses.spacer} />
        <MobileUserInfo onProfileMenuOpenClick={handleOpenProfileMenuClick} />
      </MobileNavigationRoot>
      <MobileProfileMenuDialog
        open={profileMenuOpen}
        onClose={handleCloseProfileMenuClick}
        onLogoutClick={handleLogoutClick}
      />
      <MobilePreferences open={preferencesOpen} onClose={handleClosePreferencesMenuClick} />
    </>
  );
};

export default MobileNavigation;
